import "./RiskRowDetailComponent.scss";
import {IOnChanges, IOnChangesObject, IOnDestroy, IOnInit} from "angular";
import {CurrentUserService} from "../../../../../services/user/CurrentUserService";
import {MarginLevel} from "../../../models/MarginLevel";
import {FeatureNames} from "../../../../../types/UserConfig";
import {ViewModelRow} from "../../../models/view-model/RiskDashboardViewModel";
import {RiskDashboardViewStore} from "../../../store/RiskDashboardViewStore";
import {RiskRowEntityType} from "../../../models/RiskRowEntityType";
import {ComponentCallback} from "../../../../../types/AngularJS";
import {RiskLimits} from "../../../models/RiskSummaryRecord";
import {ReplaySubject, Scheduler} from "rxjs";

export const RiskRowDetail = "riskRowDetail";

export function riskRowDetail() {
    return {
        templateUrl: "app/risk/dashboard/components/grid/detail/RiskRowDetailComponent.html",
        transclude: true,
        bindings: {
            detail: "<",
            viewStore: "<",
            onNameClick: "&"
        },
        controllerAs: "ctrl",
        controller: RiskRowDetailComponentController
    };
}

export class RiskRowDetailComponentController implements IOnInit, IOnChanges, IOnDestroy {

    public readonly detail: ViewModelRow;
    public readonly viewStore: RiskDashboardViewStore;
    public readonly onNameClick: ComponentCallback;

    public isReadOnly = true;
    public showLevels = false;

    public stelLimit: number | null = null;
    public cbplLimit: number | null = null;
    public marginLimit: number | null = null;
    public marginLevel: MarginLevel | null = null;

    public hasChanges = false;
    public levels = MarginLevel;

    private readonly updated$ = new ReplaySubject<Partial<RiskLimits>>(1);
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private currentUserService: CurrentUserService) {
        const isAdmin = currentUserService.hasFeature(FeatureNames.CLEARING_DASHBOARD_ADMIN);
        this.isReadOnly = !isAdmin;
    }

    public $onInit(): void {
        this.updated$
            .takeUntil(this.destroyed$)
            .bufferTime(200, Scheduler.async)
            .filter((changes) => changes.length > 0)
            .map((changes) => this.mergeChanges(changes))
            .scan((acc, next) => Object.assign(acc, next), {})
            .subscribe((changes) => this.viewStore.updateValues(this.detail.type, this.detail.id, changes));
    }

    private mergeChanges(changes: Array<Partial<RiskLimits>>): Partial<RiskLimits> {
        return changes.reduce((acc, next) => {
            return Object.assign({}, acc, next);
        }, {});
    }

    public $onChanges(changes: IOnChangesObject): void {
        if (changes.detail != null && this.detail != null) {
            this.showLevels = this.detail.type === RiskRowEntityType.CLIENT;
            this.hasChanges = this.detail.hasUnsavedChanges();

            this.stelLimit = this.detail.stelLimit;
            this.cbplLimit = this.detail.cbplLimit;
            this.marginLimit = this.detail.marginLimit;
            this.marginLevel = this.detail.marginLevel;
        }
    }

    public updateStelLimit() {
        const changes = {stelLimit: this.stelLimit};
        this.updated(changes);
    }

    public updateCbplLimit() {
        const changes = {cbplLimit: this.cbplLimit};
        this.updated(changes);
    }

    public updateMarginLimit() {
        const changes = {marginLimit: this.marginLimit};
        this.updated(changes);
    }

    public changeMarginLevel(level: MarginLevel) {
        const changes = {marginLevel: level};
        this.updated(changes);
    }

    private updated(changes: Partial<RiskLimits>) {
        this.updated$.next(changes);
    }

    public save() {
        if (this.detail.type === RiskRowEntityType.CLIENT) {
            this.viewStore.persistPortfolioChanges(this.detail.id);
        } else {
            this.viewStore.persistOrgChanges(this.detail.type, this.detail.id);
        }
    }

    public reset() {
        this.viewStore.reset(this.detail.type, this.detail.id);
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
